/**
 * ChipSelect (#140): selected options as removable pills plus a "+ add"
 * picker for the remaining ones (countries in the Compare panel). Reports the
 * selected codes through onChange; setSelected re-renders silently.
 */

import type { TranslationParams } from '../i18n/ui-manager.types';

export interface ChipSelectOptions {
  t: (key: string, params?: TranslationParams) => string;
  options: { code: string; label: string }[];
  selected: string[];
  onChange(codes: string[]): void;
  addLabelKey: string;
}

export interface ChipSelect {
  readonly element: HTMLElement;
  setSelected(codes: string[]): void;
}

export function createChipSelect(opts: ChipSelectOptions): ChipSelect {
  const { t, options, onChange, addLabelKey } = opts;
  let selected = [...opts.selected];
  const labelOf = (code: string) =>
    options.find((o) => o.code === code)?.label ?? code;

  const element = document.createElement('div');
  element.className = 'chip-select';

  const render = () => {
    element.replaceChildren();
    for (const code of selected) {
      const chip = document.createElement('span');
      chip.className = 'chip-select__chip';
      chip.dataset.chip = code;
      const text = document.createElement('span');
      text.textContent = labelOf(code);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'chip-select__remove';
      remove.dataset.chipRemove = code;
      remove.textContent = '✕';
      remove.setAttribute(
        'aria-label',
        t('compare.removeChip', { label: labelOf(code) })
      );
      remove.addEventListener('click', () => {
        selected = selected.filter((c) => c !== code);
        render();
        onChange([...selected]);
      });
      chip.append(text, remove);
      element.append(chip);
    }
    const remaining = options.filter((o) => !selected.includes(o.code));
    if (remaining.length === 0) return;
    const select = document.createElement('select');
    select.className = 'chip-select__add';
    select.dataset.chipAdd = '';
    select.setAttribute('aria-label', t(addLabelKey));
    const first = document.createElement('option');
    first.value = '';
    first.textContent = t(addLabelKey);
    select.append(first);
    for (const o of remaining) {
      const opt = document.createElement('option');
      opt.value = o.code;
      opt.textContent = o.label;
      select.append(opt);
    }
    select.addEventListener('change', () => {
      if (!select.value) return;
      selected = [...selected, select.value];
      render();
      onChange([...selected]);
    });
    element.append(select);
  };

  render();

  return {
    element,
    setSelected(codes) {
      selected = [...codes];
      render();
    },
  };
}
